import {translations} from 'presentation/translations-new';
import {ChangeEvent, DragEvent, MouseEvent, useState} from 'react';
import {ALLOWED_EXTENSIONS} from '../constants';
import {useExternalErrorMessageStore} from '../store/useExternalErrorMessageStore';
import {UseFileUploadProps} from '../types';
import {SendingFilesSchema} from '../validations';

const {
	validations: {onlyOneFile},
} = translations['pt-br'].others.uploadFiles;

export const useFileHandler = ({onChangeFile, removeFile, isUploading}: UseFileUploadProps) => {
	const [fileIdState, setFileIdState] = useState('');
	const [errorState, setErrorState] = useState<string | null>(null);
	const [isConfirmActionModalOpenState, setIsConfirmActionModalOpenState] = useState(false);

	const [isOnDropState, setIsOnDropState] = useState(false);
	const [isAttachmentInstructionModalOpenState, setIsAttachmentInstructionModalOpenState] =
		useState(false);
	const [isAttachmentInstructionCheckedState, setIsAttachmentInstructionCheckedState] =
		useState(false);
	const [isDragAndDropState, setIsDragAndDropState] = useState(false);

	const allowedExtensions = ALLOWED_EXTENSIONS.join(', ');

	const externalErrorMessage = useExternalErrorMessageStore(state => state.externalErrorMessage);
	const resetExternalErrorMessage = useExternalErrorMessageStore(
		state => state.resetExternalErrorMessage,
	);

	const handleToggleConfirmActionModal = () => {
		setIsConfirmActionModalOpenState(prev => !prev);
	};

	const handleToggleConfirmAction = (id: string) => {
		setFileIdState(id);
	};

	const clearExternalErrorMessage = () => {
		if (externalErrorMessage) resetExternalErrorMessage();
	};

	const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
		clearExternalErrorMessage();
		setErrorState(null);
		const {files} = e.target;

		if (!files) return;

		const filesArray = Array.from(files);
		const result = SendingFilesSchema.safeParse(files[0]);

		if (!result.success) {
			setErrorState(result.error.issues[0].message);
			return;
		}

		onChangeFile(filesArray);

		const {target} = e;
		target.value = '';
		setIsOnDropState(false);
	};

	const handleOnDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragAndDropState(true);

		clearExternalErrorMessage();
		if (isUploading) return;

		setErrorState(null);

		if (!isAttachmentInstructionCheckedState) {
			setIsOnDropState(false);
			setIsAttachmentInstructionModalOpenState(true);
			return;
		}

		const {files} = event.dataTransfer;
		if (!files) return;

		const filesArray = Array.from(files);
		if (filesArray.length > 1) {
			setErrorState(onlyOneFile);
			setIsOnDropState(false);
			return;
		}

		const result = SendingFilesSchema.safeParse(filesArray[0]);
		if (!result.success) {
			setErrorState(result.error.issues[0].message);
			return;
		}

		onChangeFile(filesArray);
		setIsOnDropState(false);
		setIsDragAndDropState(false);
	};

	const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(true);
		setErrorState(null);
		clearExternalErrorMessage();
	};

	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsOnDropState(false);
		setErrorState(null);
		clearExternalErrorMessage();
	};

	const handleUploadByClick = (e: MouseEvent<HTMLInputElement>) => {
		setErrorState(null);
		setIsDragAndDropState(false);
		clearExternalErrorMessage();

		if (!isAttachmentInstructionCheckedState) {
			e.preventDefault();
			setIsAttachmentInstructionModalOpenState(true);
		}
	};

	const handleConfirmModal = () => {
		removeFile(fileIdState);
		handleToggleConfirmActionModal();
	};

	return {
		fileIdState,
		errorState,
		isConfirmActionModalOpenState,
		isOnDropState,
		allowedExtensions,
		handleToggleConfirmActionModal,
		handleToggleConfirmAction,
		handleOnChange,
		handleOnDrop,
		handleDragOver,
		handleDragLeave,
		handleUploadByClick,
		handleConfirmModal,
		setIsConfirmActionModalOpenState,
		isDragAndDropState,
		setIsAttachmentInstructionModalOpenState,
		isAttachmentInstructionModalOpenState,
		setIsAttachmentInstructionCheckedState,
		isAttachmentInstructionCheckedState,
	};
};
